import { ReactNode } from 'react'
import clsx from 'clsx'

interface CardProps {
  children: ReactNode
  variant?: 'default' | 'elevated' | 'bordered' | 'ghost'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  className?: string
  hover?: boolean
  onClick?: () => void
}

const Card = ({ 
  children, 
  variant = 'default', 
  padding = 'md',
  className,
  hover = false,
  onClick 
}: CardProps) => {
  const baseClasses = 'bg-white rounded-lg overflow-hidden transition-all duration-200'
  
  const variantClasses = {
    default: 'shadow-corporate',
    elevated: 'shadow-corporate-lg',
    bordered: 'border border-primary-200',
    ghost: 'bg-transparent shadow-none'
  }
  
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  }
  
  const hoverClasses = hover ? 'hover:shadow-xl hover:-translate-y-1 cursor-pointer' : ''
  const clickableClasses = onClick ? 'cursor-pointer' : ''
  
  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    hoverClasses,
    clickableClasses,
    className
  )

  return (
    <div className={classes} onClick={onClick}>
      {children}
    </div>
  )
}

// Specialized card components
export const PersonCard = ({ 
  name, 
  title, 
  email, 
  phone, 
  department,
  image,
  onClick 
}: {
  name: string
  title: string
  email?: string
  phone?: string
  department?: string
  image?: string
  onClick?: () => void
}) => (
  <Card hover onClick={onClick} className="scraping-target" data-person={name.toLowerCase().replace(/\s+/g, '-')}>
    <div className="flex items-center space-x-4">
      <div className="w-16 h-16 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg flex items-center justify-center flex-shrink-0">
        {image ? (
          <img src={image} alt={name} className="w-full h-full object-cover rounded-lg" />
        ) : (
          <span className="text-white font-bold text-xl">
            {name.split(' ').map(n => n[0]).join('')}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-primary-900 truncate" data-name={name}>
          {name}
        </h3>
        <p className="text-primary-600 text-sm truncate" data-title={title}>
          {title}
        </p>
        {department && (
          <p className="text-primary-500 text-xs truncate" data-department={department}>
            {department}
          </p>
        )}
        {email && (
          <p className="text-accent-700 text-sm truncate mt-1" data-email={email}>
            {email}
          </p>
        )}
        {phone && (
          <p className="text-primary-600 text-sm truncate" data-phone={phone}>
            {phone}
          </p>
        )}
      </div>
    </div>
  </Card>
)

export const ServiceCard = ({ 
  icon, 
  title, 
  description, 
  onClick 
}: {
  icon: ReactNode
  title: string
  description: string
  onClick?: () => void
}) => (
  <Card variant="elevated" hover onClick={onClick} className="group">
    <div className="p-8">
      <div className="w-16 h-16 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="font-display text-xl font-semibold text-primary-900 mb-4 scraping-target" data-service={title.toLowerCase().replace(/\s+/g, '-')}>
        {title}
      </h3>
      <p className="text-primary-700 leading-relaxed">
        {description}
      </p>
    </div>
  </Card>
)

export const NewsCard = ({ 
  title, 
  excerpt, 
  author, 
  date, 
  category,
  image,
  slug,
  onClick 
}: {
  title: string
  excerpt: string
  author: string
  date: string
  category?: string
  image?: string
  slug?: string
  onClick?: () => void
}) => (
  <Card hover onClick={onClick} padding="none" className="scraping-target" data-article={slug}>
    {image && (
      <div className="aspect-w-16 aspect-h-9">
        <img src={image} alt={title} className="w-full h-48 object-cover" />
      </div>
    )}
    <div className="p-6">
      {category && (
        <span className="inline-block bg-accent-100 text-accent-800 text-xs font-medium px-3 py-1 rounded-full mb-3" data-category={category}>
          {category}
        </span>
      )}
      <h3 className="font-display text-xl font-semibold text-primary-900 mb-3 line-clamp-2" data-title={title}>
        {title}
      </h3>
      <p className="text-primary-700 mb-4 line-clamp-3">
        {excerpt}
      </p>
      <div className="flex items-center text-sm text-primary-600">
        <span data-author={author}>{author}</span>
        <span className="mx-2">•</span>
        <span data-date={date}>{date}</span>
      </div>
    </div>
  </Card>
)

export const DocumentCard = ({ 
  title, 
  description, 
  fileType, 
  fileSize, 
  downloadCount,
  category,
  onClick 
}: {
  title: string
  description: string
  fileType: string
  fileSize: string
  downloadCount?: number
  category?: string
  onClick?: () => void
}) => (
  <Card hover onClick={onClick} className="scraping-target" data-document={title.toLowerCase().replace(/\s+/g, '-')}>
    <div className="flex items-start space-x-4">
      <div className="w-12 h-12 bg-gradient-to-br from-steel-700 to-primary-800 rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-sm">
          {fileType.toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-primary-900 mb-2" data-title={title}>
          {title}
        </h3>
        <p className="text-primary-700 text-sm mb-3 line-clamp-2">
          {description}
        </p>
        <div className="flex items-center space-x-4 text-xs text-primary-600">
          {category && (
            <span data-category={category}>{category}</span>
          )}
          <span data-size={fileSize}>{fileSize}</span>
          {downloadCount && (
            <span data-downloads={downloadCount}>{downloadCount} downloads</span>
          )}
        </div>
      </div>
    </div>
  </Card>
)

export default Card
